import React, { FC } from 'react';
import { toast } from 'react-toastify';
import { Button, Card, CardBody, CardHeader, Col, Row, Table } from 'reactstrap';
import { ExportListItem } from '../../../../shared/export';
import { ActionButtonModal, DateTime, MessageCard } from '../../../components';
import { TranslationKey, useTranslation } from '../../../translations';
import {
  Fetcher,
  renderName,
  useConfig,
  useFetcher,
  useForm,
  useQuery,
  useUserSafe,
} from '../../../utils';

export const ExportSettings: FC = () => {
  const { ts } = useTranslation();
  const req = useFetcher(async () => {
    const exportRequests = await api.pendingExportRequests();
    return { exportRequests };
  }, []);

  if (!req.success) {
    return <Fetcher result={req} />;
  }

  const { exportRequests } = req.data;

  return (
    <>
      <h3>{ts('export_data')}</h3>
      <br />
      <Row>
        <ExportHelpBlock pendingRequests={exportRequests} reload={req.refresh} />
        <Col xs={12} />
        <PendingRequestsTable reload={req.refresh} requests={exportRequests} />
        <Col xs={12} />
        <ExportList requests={exportRequests} />
      </Row>
    </>
  );
};

interface ExportHelpBlockProps {
  readonly pendingRequests: ExportListItem[];
  readonly reload: () => void;
}

const ExportHelpBlock: FC<ExportHelpBlockProps> = ({ pendingRequests, reload }) => {
  const user = useUserSafe();
  const { ts } = useTranslation();
  const {
    organization: { approvalDirect, forceExportPii },
  } = useConfig();

  const pendingApproval = pendingRequests.some(
    (request) => request.status === 'waiting' && request.requester.id === user.id,
  );

  const userApproved = pendingRequests.some(
    (request) => request.status === 'approved' && request.requester.id === user.id,
  );

  const form = useForm(async () => {
    await api.requestExportPiiAccess();
    reload();
  }, [reload]);

  if (forceExportPii) {
    return;
  }

  return (
    <Col className="mb-10" lg={12} xs={12}>
      <Card>
        <CardBody>
          <p>{ts('export_the_convention_data_from')}</p>
          <p>{ts('data_exports_may_contain_personal')}</p>
          <p>
            {ts(
              approvalDirect
                ? 'for_security_and_user_privacy'
                : 'for_security_and_user_privacy_data_export_requires_a_hour_delay_in_which_any_other_admin_can_cancel_the_export_request',
            )}
          </p>
          <form onSubmit={form.onSubmit}>
            <Button
              color={userApproved ? 'success' : 'primary'}
              disabled={pendingApproval || userApproved}
              id="requestExport"
            >
              {pendingApproval ? 'Pending PII Access approval' : 'Request PII Access'}
            </Button>
          </form>

          {pendingApproval && <p>{ts('you_can_ask_an_authorized')}</p>}
        </CardBody>
      </Card>
    </Col>
  );
};

interface PendingRequestsTableProps {
  readonly requests: ExportListItem[];
  readonly reload: () => void;
}

export const PendingRequestsTable: FC<PendingRequestsTableProps> = ({ requests, reload }) => {
  const { ts } = useTranslation();
  const pendingRequests = requests.filter((t) => t.status === 'waiting');
  if (pendingRequests.length === 0) {
    return null;
  }

  return (
    <Col className="mb-10" lg={12} xs={12}>
      <Card>
        <CardHeader>{ts('pending_requests')}</CardHeader>
        <CardBody>
          <Table>
            <thead>
              <tr>
                <th>{ts('datetime_created')}</th>
                <th>{ts('name_of_requesting_user')}</th>
                <th>{ts('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {pendingRequests.map((request) => (
                <tr key={request.id}>
                  <td>
                    <DateTime value={request.createdAt} />
                  </td>
                  <td>{renderName(request.requester)}</td>
                  <td>
                    <RequestButtons onChange={reload} request={request} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </Col>
  );
};

const RequestButtons: FC<{ readonly request: ExportListItem; readonly onChange: () => void }> = ({
  request,
  onChange,
}) => {
  const { ts } = useTranslation();
  const user = useUserSafe();
  const {
    organization: { approvalDirect },
  } = useConfig();

  if (user.id === request.requester.id) {
    return (
      <ActionButtonModal
        actionContent="common.delete"
        buttonContent="common.delete"
        className="indent"
        color="primary"
        id={`delete-${request.id}`}
        onComplete={async () => {
          await api.deleteExportRequest(request.id);
          toast.success(ts('request_deleted'));
          onChange();
        }}
        title="delete_request"
      >
        {ts('are_you_sure_you_want_to_delete_your_request')}
      </ActionButtonModal>
    );
  }

  if (approvalDirect) {
    return (
      <>
        <ActionButtonModal
          actionContent="common.approve"
          buttonContent="common.approve"
          className="indent"
          color="primary"
          id={`approve-${request.id}`}
          onComplete={async () => {
            await api.approveExportRequest(request.id);
            toast.success(ts('request_approved'));
            onChange();
          }}
          title="approve_request"
        >
          {ts('are_you_sure_you_want_to_approve_request', renderName(request.requester))}
        </ActionButtonModal>
        <ActionButtonModal
          actionContent="common.reject"
          buttonContent="common.reject"
          className="indent"
          color="danger"
          id={`reject-${request.id}`}
          onComplete={async () => {
            await api.rejectExportRequest(request.id);
            toast.success(ts('request_rejected'));
            onChange();
          }}
          title="reject_request"
        >
          {ts('are_you_sure_you_want_to_reject_request', renderName(request.requester))}
        </ActionButtonModal>
      </>
    );
  }

  return (
    <ActionButtonModal
      actionContent="common.reject"
      buttonContent="common.reject"
      className="indent"
      color="danger"
      id={`reject-${request.id}`}
      onComplete={async () => {
        await api.rejectExportRequest(request.id);
        toast.success(ts('request_rejected'));
        onChange();
      }}
      title="reject_request"
    >
      {ts('are_you_sure_you_want_to_reject_request', renderName(request.requester))}
    </ActionButtonModal>
  );
};

export const ExportBlock: FC = () => {
  const { ts } = useTranslation();
  const id = useQuery().get('code');

  const fetcher = useFetcher(async () => {
    if (!id) {
      return;
    }

    await api.rejectExportRequest(id);
  });

  if (!id) {
    return (
      <MessageCard icon="offline_bolt" level="danger" title="`code` not found on the url query" />
    );
  }

  if (!fetcher.success) {
    return <Fetcher result={fetcher} />;
  }

  return <div id="rejectedSuccess">{ts('the_data_export_request_id', id)}</div>;
};

interface ExportListProps {
  readonly requests: ExportListItem[];
}

const ExportList: FC<ExportListProps> = ({ requests }) => {
  const { ts } = useTranslation();
  const user = useUserSafe();
  const {
    organization: { forceExportPii },
  } = useConfig();

  const isApproved = requests.some(
    (request) => request.status === 'approved' && request.requester.id === user.id,
  );

  const exportOptions: { type: string; description: TranslationKey }[] = [];

  if (isApproved || forceExportPii) {
    exportOptions.push({
      type: 'user',
      description: 'export_all_user_data_including',
    });
  }

  exportOptions.push(
    {
      type: 'registration',
      description: 'export_registration_data_including_user',
    },
    {
      type: 'volunteer',
      description: 'export_volunteer_data_their_names',
    },
    {
      type: 'dealer',
      description: 'export_dealer_data_their_names',
    },
    {
      type: 'orderItems',
      description: 'export_order_items_data_include',
    },
    {
      type: 'orders',
      description: 'export_order_data_include_each',
    },
  );

  return exportOptions.map(({ type, description }) => (
    <Col className="mb-10" key={type} lg={12} xs={12}>
      <Card>
        <CardBody id={`export-${type}`}>
          <p>{ts(description)}</p>
          <ExportButtons path={`/api/export/download/${type}`} />
        </CardBody>
      </Card>
    </Col>
  ));
};

export const ExportButtons: FC<{ readonly path: string }> = ({ path }) => {
  return (
    <div>
      <Button color="primary" href={`${path}?format=csv`} tag="a">
        CSV
      </Button>{' '}
      <Button color="primary" href={`${path}?format=json`} tag="a">
        JSON
      </Button>
    </div>
  );
};
